import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  canProposeOnThread,
  ProposeRefusal,
} from '@rolebase/shared/helpers/canProposeOnThread'
import { Visibility_Enum } from '@gql'
import { getProposalScope } from '@rolebase/shared/helpers/getProposalScope'
import { useMemo } from 'react'

// Only the visibility level of the tension is read here, so callers may pass
// anything carrying it — a full thread, or a form's current values.
interface ThreadVisibility {
  visibility: Visibility_Enum
}

export interface CanPropose {
  canPropose: boolean
  // Why it was refused, so the caller picks the right message without
  // rederiving the rule. Undefined when nothing is refused.
  refusal?: ProposeRefusal
  // The circles that would decide, named so a refusal can say which one it is
  // about. Two of them only for a tension carried by a link.
  decidingCircleNames: string[]
}

// Whether the current member may attach a proposal to a tension, and which
// circles would decide it (Yocoop, 09/08/2026).
//
// One rule: you propose where you vote. The perimeter is the deciding circles,
// kept only where the member holds a role — in shared governance those who act
// are those who decide, and someone outside the circle submits their idea to
// the people in it rather than legislating for them.
//
// The names come back even when the right is missing: a refusal that cannot say
// which circle decides is a refusal nobody can act on.
//
// The tension is optional (GOUV-16, D11). Passing it adds the privacy rule of
// `canProposeOnThread`; omitting it leaves the behaviour untouched, which is
// what a role's Security menu needs — there is no tension there to have a level.
export default function useCanPropose(
  circleId?: string,
  thread?: ThreadVisibility | null
): CanPropose {
  const { orgData } = useOrgContext()
  const currentMember = useCurrentMember()

  return useMemo(() => {
    if (!orgData || !circleId || !currentMember) {
      return { canPropose: false, decidingCircleNames: [] }
    }
    const scope = getProposalScope(orgData, circleId)
    const allowed = scope.filter((id) =>
      orgData.getParticipants(id).some((p) => p.member.id === currentMember.id)
    )
    return {
      ...canProposeOnThread(thread, allowed.length > 0),
      decidingCircleNames: scope
        .map((id) => orgData.getRole(orgData.getCircle(id)?.roleId)?.name)
        .filter((name): name is string => !!name),
    }
  }, [orgData, circleId, currentMember, thread?.visibility])
}
